import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import queryString from 'query-string';

import { omit } from 'helpers/lodashReplacement';
import MartyLink from 'components/common/MartyLink';
import OrderHistoryCard from 'components/account/OrderHistoryCard';
import styles from 'styles/components/account/orderHistoryCardLayout.scss';

const makePreviousPage = (pathname, queryParams, testId) => {
  const { page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;
  const previousPageParams = queryString.stringify({ ...queryParams, page: currentPage - 1 });

  return (
    <MartyLink
      to={`${pathname}?${previousPageParams}`}
      className={cn({ [styles.disabled]: currentPage === 1 })}
      disabled={currentPage === 1}
      data-test-id={testId('prevButton')}>
      Prev
    </MartyLink>
  );
};

const makeCurrentPage = (totalPages, queryParams, testId) => {
  const { page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;
  return <div data-test-id={testId('currentPage')}>{currentPage} of {totalPages}</div>;
};

const makeNextPage = (totalPages, pathname, queryParams, testId) => {
  const { page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;
  const nextPageParams = queryString.stringify({ ...queryParams, page: currentPage + 1 });

  return (
    <MartyLink
      to={`${pathname}?${nextPageParams}`}
      className={cn({ [styles.disabled]: currentPage === totalPages })}
      disabled={currentPage === totalPages}
      data-test-id={testId('nextButton')}>
      Next
    </MartyLink>
  );
};

const OrderHistoryCardLayout = ({ orders, location, totalPages }, { testId }) => {
  const { pathname, search } = location;

  // Remove `overview` param, it's a flag used by the desktop order history pagination
  const queryParams = omit(queryString.parse(search), 'overview');

  if (orders.length) {
    return (
      <div>
        <ul key="orders" role="group">
          {orders.map(order => (
            <OrderHistoryCard key={order.orderId} order={order}/>
          ))}
        </ul>
        <div className={styles.pagination}>
          {makePreviousPage(pathname, queryParams, testId)}
          {makeCurrentPage(totalPages, queryParams, testId)}
          {makeNextPage(totalPages, pathname, queryParams, testId)}
        </div>
      </div>
    );
  } else {
    return (
      <div className={styles.empty}>
        Your order history is empty.
      </div>
    );
  }
};

OrderHistoryCardLayout.contextTypes = {
  testId: PropTypes.func
};

OrderHistoryCardLayout.propTypes = {
  orders: PropTypes.array.isRequired,
  location: PropTypes.object.isRequired,
  totalPages: PropTypes.number.isRequired
};

export default OrderHistoryCardLayout;
